import { useState, useEffect, useCallback, useRef } from "react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { Phone, MapPin, X, ChevronUp, Star, Utensils, Compass, TreePine, Sparkles, Car, Heart, Moon, ShoppingBag, Leaf } from "lucide-react";
import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";

const API_URL = '';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const GOLD_COAST_CENTER = { lat: -28.0167, lng: 153.4000 };

// Dark map style — high contrast, minimal
const DARK_MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#1a1a2e" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#4a4a6a" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a2e" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#2a2a4a" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#1e1e38" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#3a3a5a" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#1a2a1a" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#22223a" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#2a2a4a" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2a2a4a" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#3a3a5a" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#1e1e38" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0a1628" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#1a3a5a" }] },
];

// Light map style
const LIGHT_MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#eeeeee" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e5e5e5" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dadada" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9e4f0" }] },
];

// Muted Material You palette — sophisticated, not neon
const MARKER_COLORS = {
  eat: { bg: '#DC6B4A', light: '#FCEAE5', icon: Utensils },        // Warm coral
  do: { bg: '#7B68AD', light: '#EDE8F5', icon: Sparkles },         // Muted violet
  explore: { bg: '#5A9A6E', light: '#E4F0E8', icon: Compass },     // Sage green
  experiences: { bg: '#C4635F', light: '#F5E3E2', icon: TreePine }, // Dusty rose
  carhire: { bg: '#5182AB', light: '#E1ECF5', icon: Car },         // Slate blue
  wellness: { bg: '#4A9A96', light: '#E0F0EF', icon: Leaf },      // Muted teal
  nightlife: { bg: '#9B6B9E', light: '#F0E4F1', icon: Moon },      // Soft plum
  shopping: { bg: '#B8923E', light: '#F3ECE0', icon: ShoppingBag },// Warm amber
};

// Map Pin — Google Maps style teardrop with category icon
const BusinessPin = ({ business, isSelected, isDark }) => {
  const isFeatured = business.is_featured;
  const cat = MARKER_COLORS[business.category] || MARKER_COLORS.explore;
  const Icon = cat.icon;
  const pinSize = isFeatured ? 44 : 36;
  const iconSize = isFeatured ? 16 : 13;

  return (
    <div 
      className="cursor-pointer transition-transform duration-150"
      style={{ 
        transform: isSelected ? 'scale(1.2) translateY(-4px)' : 'scale(1)',
        filter: isSelected ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
      }}
    >
      {/* Teardrop pin shape */}
      <svg width={pinSize} height={pinSize * 1.3} viewBox="0 0 36 47" fill="none">
        <path 
          d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 29 18 29s18-15.5 18-29C36 8.06 27.94 0 18 0z" 
          fill={isDark ? cat.bg : cat.bg}
          opacity={isDark ? 0.95 : 1}
        />
        {/* White inner circle */}
        <circle cx="18" cy="17" r="11" fill="white" opacity={isDark ? 0.95 : 1} />
      </svg>
      {/* Category icon centered in the white circle */}
      <div 
        className="absolute flex items-center justify-center"
        style={{
          top: pinSize * 0.17,
          left: '50%',
          transform: 'translateX(-50%)',
          width: iconSize * 1.6,
          height: iconSize * 1.6,
        }}
      >
        <Icon 
          style={{ width: iconSize, height: iconSize, color: cat.bg }} 
          strokeWidth={2.2} 
        />
      </div>
      {/* Featured badge */}
      {isFeatured && (
        <div 
          className="absolute bg-amber-500 rounded-full flex items-center justify-center border-2 border-white"
          style={{ width: 16, height: 16, top: -2, right: -2 }}
        >
          <Star className="w-2 h-2 text-white fill-white" />
        </div>
      )}
    </div>
  );
};

// Bottom Sheet Component
const BottomSheet = ({ business, onClose, isDark }) => {
  if (!business) return null;
  
  const handleCall = (e) => {
    e.stopPropagation();
    window.location.href = `tel:${business.phone}`;
  };
  
  const handleOpenMaps = (e) => {
    e.stopPropagation();
    const query = encodeURIComponent(business.address);
    window.open(`https://maps.google.com/?q=${query}`, '_blank', 'noopener,noreferrer');
  };
  
  const markerColor = MARKER_COLORS[business.category] || MARKER_COLORS.explore;

  return (
    <div className="dq-map-sheet absolute left-0 right-0 z-30 px-4">
      <div className={`rounded-2xl overflow-hidden max-w-lg mx-auto backdrop-blur-xl border ${
        isDark 
          ? 'bg-[#1E1E1E]/95 border-white/[0.08] shadow-2xl shadow-black/40' 
          : 'bg-white border-slate-100 shadow-xl'
      }`}>
        {/* Handle bar */}
        <div className="flex justify-center pt-2 pb-1">
          <div className={`w-10 h-1 rounded-full ${isDark ? 'bg-white/20' : 'bg-slate-200'}`} />
        </div>
        
        {/* Close button */}
        <button
          aria-label="Close place details" onClick={onClose}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center ${
            isDark ? 'bg-white/10 hover:bg-white/15' : 'bg-slate-100'
          }`}
        >
          <X className={`w-4 h-4 ${isDark ? 'text-white/70' : 'text-slate-500'}`} />
        </button>
        
        {/* Content */}
        <div className="px-4 pb-4">
          <div className="flex items-start gap-3">
            <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
              <img
                src={business.image_url || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=200'}
                alt={business.name}
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="flex-1 min-w-0 pt-1">
              <div className="flex items-center gap-2">
                <h3 className={`text-base font-semibold font-heading truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {business.name}
                </h3>
                {business.is_featured && (
                  <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-amber-500 text-white flex items-center gap-0.5">
                    <Star className="w-2 h-2" />
                    Featured
                  </span>
                )}
              </div>
              <p className="text-sm text-teal-600 capitalize">{business.category}</p>
              <p className="text-xs text-slate-500 truncate mt-0.5">{business.address}</p>
            </div>
          </div>
          
          {/* Actions - All businesses have access to Call and Map */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleCall}
              className="flex-1 h-11 rounded-full bg-teal-600 text-white font-semibold text-sm flex items-center justify-center gap-2 active:bg-teal-700 transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call
            </button>
            
            <button
              onClick={handleOpenMaps}
              className="flex-1 h-11 rounded-full bg-slate-100 text-slate-700 font-semibold text-sm flex items-center justify-center gap-2 active:bg-slate-200 transition-colors"
            >
              <MapPin className="w-4 h-4" />
              Open in Maps
            </button>
          </div>
          
          {/* View details link */}
          <Link 
            to={`/business/${business.business_id}`}
            className="block text-center text-sm text-teal-600 font-medium mt-3"
          >
            View full details →
          </Link>
        </div>
      </div>
    </div>
  );
};

// Map Content Component (needs to be inside APIProvider)
const MapContent = ({ businesses, selectedBusiness, onSelectBusiness, isDark }) => {
  const map = useMap();
  
  useEffect(() => {
    if (map && businesses.length > 0) {
      // Fit map to show all markers
      const bounds = new window.google.maps.LatLngBounds();
      businesses.forEach(business => {
        if (business.latitude && business.longitude) {
          bounds.extend({ lat: business.latitude, lng: business.longitude });
        }
      });
      
      // Only fit bounds if we have valid coordinates
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 50 });
      }
    }
  }, [map, businesses]);
  
  return (
    <>
      {businesses.map((business) => {
        if (!business.latitude || !business.longitude) return null;
        
        return (
          <AdvancedMarker
            key={business.business_id}
            position={{ lat: business.latitude, lng: business.longitude }}
            onClick={() => onSelectBusiness(business)}
            clickable={true}
          >
            <BusinessPin 
              business={business} 
              isSelected={selectedBusiness?.business_id === business.business_id}
              isDark={isDark}
            />
          </AdvancedMarker>
        );
      })}
    </>
  );
};

export default function MapPage() {
  const { isDark } = useTheme();
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapError, setMapError] = useState(null);
  const mapContainerRef = useRef(null);

  // Analytics tracking helper
  const trackMapEvent = async (businessId) => {
    try {
      await fetch(`${API_URL}/api/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: 'map_open', business_id: businessId })
      });
    } catch (error) {
      console.debug('Map analytics tracking failed:', error);
    }
  };

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/businesses`);
        if (response.ok) {
          const data = await response.json();
          setBusinesses(data);
        }
      } catch (error) {
        console.error('Failed to fetch businesses:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinesses();
  }, []);

  const handleSelectBusiness = useCallback((business) => {
    setSelectedBusiness(prev => {
      const isSelecting = prev?.business_id !== business.business_id;
      if (isSelecting) {
        trackMapEvent(business.business_id);
      }
      return prev?.business_id === business.business_id ? null : business;
    });
  }, []);

  const handleCloseSheet = useCallback(() => {
    setSelectedBusiness(null);
  }, []);

  // Check if API key is available
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="min-h-screen bg-slate-50/50 pb-24 flex items-center justify-center" data-testid="map-page">
        <div className="text-center px-6">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-8 h-8 text-slate-300" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 font-heading mb-2">
            Map unavailable
          </h2>
          <p className="text-sm text-slate-500 font-body">
            The interactive map is unavailable right now.
          </p>
          <p className="text-xs text-slate-400 font-body mt-2">
            You can still browse places and open directions from their details.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#121212]' : 'bg-white'}`}>
        <div className={`w-6 h-6 border-2 ${isDark ? 'border-blue-500' : 'border-teal-600'} border-t-transparent rounded-full animate-spin`} />
      </div>
    );
  }

  return (
    <div className={`h-screen pb-20 relative ${isDark ? 'bg-[#121212]' : 'bg-white'}`} data-testid="map-page" ref={mapContainerRef}>
      {/* Header */}
      <header className={`absolute top-0 left-0 right-0 z-20 backdrop-blur-xl safe-top ${
        isDark ? 'bg-[#121212]/80 border-b border-white/[0.04]' : 'bg-white/90 border-b border-slate-100/80'
      }`}>
        <div className="px-4 py-3">
          <h1 className={`text-lg font-semibold font-heading ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Map
          </h1>
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {businesses.length} places
          </p>
        </div>
      </header>

      {/* Map */}
      <div className="absolute inset-0 pt-16 pb-20">
        <APIProvider 
          apiKey={GOOGLE_MAPS_API_KEY}
          onLoad={() => console.log('Maps API loaded')}
        >
          <Map
            defaultCenter={GOLD_COAST_CENTER}
            defaultZoom={11}
            gestureHandling="greedy"
            disableDefaultUI={true}
            clickableIcons={false}
            mapId="disqover-map"
            colorScheme={isDark ? 'DARK' : 'LIGHT'}
            className="w-full h-full"
            onClick={() => setSelectedBusiness(null)}
          >
            <MapContent 
              businesses={businesses}
              selectedBusiness={selectedBusiness}
              onSelectBusiness={handleSelectBusiness}
              isDark={isDark}
            />
          </Map>
        </APIProvider>
      </div>

      {/* Bottom Sheet */}
      <BottomSheet 
        business={selectedBusiness} 
        onClose={handleCloseSheet}
        isDark={isDark}
      />

      {/* Category Legend */}
      {!selectedBusiness && <div className={`dq-map-legend absolute left-4 z-20 rounded-xl px-3 py-2.5 border ${
        isDark ? 'bg-[#1E1E1E]/90 border-white/[0.08]' : 'bg-white/95 border-slate-200/80'
      }`}>
        <div className="grid grid-cols-4 gap-x-3 gap-y-1.5 text-[10px]">
          {Object.entries(MARKER_COLORS).map(([cat, { bg, icon: CatIcon }]) => (
            <div key={cat} className="flex items-center gap-1">
              <CatIcon style={{ width: 10, height: 10, color: bg }} strokeWidth={2.5} />
              <span className={`capitalize ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {cat === 'carhire' ? 'Cars' : cat}
              </span>
            </div>
          ))}
        </div>
      </div>}
    </div>
  );
}
